import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { ipManagementI18n } from './i18n';

export default function IpManagement() {
  // 1) Hooks first
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Table, Badge, IconButton } =
    DesignSystemComponents ?? {};

  const { language } = useLanguage();
  const ipLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: ipManagementI18n });

  useEffect(() => {
    ipManagementI18n.changeLanguage(ipLanguage);
  }, [ipLanguage]);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    import('./dummyIpsTable')
      .then((m) => {
        if (!mounted) return;
        setRows(m.dummyIpsTable || []);
      })
      .catch((error) => {
        console.error('Error loading IP table data:', error);
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.ip.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const onCreate = () => {
    // TODO: open "Agregar IP" modal / navigate to form
  };
  const onEdit = (row) => {
    // TODO: open edit modal / navigate with row.id
  };
  const onDelete = (row) => {
    // TODO: confirm + delete
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  // 2) Fallback if DS not provided
  if (!Card || !Button || !Input || !Table || !Badge) return null;

  const statusColor = (s) =>
    s === 'Permitido' ? 'success' : s === 'Prohibido' ? 'error' : 'warning';

  return (
    <Card specialPadding='p-4'>
      {/* Header */}
      <div className='d-flex justify-content-between mb-3'>
        <div>
          <h2 className='mb-1'>{t('ip_pageTitle')}</h2>
          <p className='caption font-light'>
            {t('ip_pageDescription')}
          </p>
        </div>
        <Button
          label={t('ip_addBtn')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={onCreate}
        />
      </div>

      {/* Search */}
      <div className='mb-3'>
        <Input
          icon='search'
          name='searchIps'
          placeholder={t('ip_searchPlaceholder')}
          type='text'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className='table-container'>
        <Table
          columns={[
            { accessor: 'ip', label: t('ip_col_ip') },
            { accessor: 'description', label: t('ip_col_description') },
            {
              accessor: 'status',
              label: t('ip_col_status'),
              render: (row) => (
                <Badge
                  text={row.status}
                  color={statusColor(row.status)}
                  size='sm'
                />
              ),
            },
            {
              label: t('ip_col_actions'),
              render: (row) => (
                <div className='d-flex gap-3'>
                  <IconButton
                    ariaLabel={t('ip_editAria', { name: row.name })}
                    icon='pencil'
                    onClick={() => onEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('ip_deleteAria', { name: row.name })}
                    icon='trash'
                    onClick={() => onDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={filtered}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          totalItems={filtered.length}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
        />
      </div>
    </Card>
  );
}
